import { useId, useState } from 'react'

import { useAnnounce } from '@/shared/providers/announcer-provider'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { Switch } from '@/ui/switch'
import { useCanEditSettings } from '../../lib/setting-queries'
import {
  assistantSettingErrorMessage,
  useAssistantDefinition,
  useAssistantPolicyResult,
  useSetAssistantEnabled,
} from './assistant-setting-queries'

export function AssistantEnabledSection({ entityId }: { entityId: string }) {
  const switchId = useId()
  const canEdit = useCanEditSettings(entityId)
  const definition = useAssistantDefinition(entityId)
  const result = useAssistantPolicyResult(entityId).data
  const save = useSetAssistantEnabled(entityId)
  const announce = useAnnounce()
  const [operationId, setOperationId] = useState(() => crypto.randomUUID())

  const own = definition.data ?? null
  const enabled = own ? own.value : true
  const applying = result !== undefined && (result.selectionStatus === 'NeverEvaluated'
    || result.refreshStatus === 'Pending' || result.refreshStatus === 'Running')

  const hintId = `${switchId}-hint`
  const change = (next: boolean) => {
    if (save.isPending) return
    save.mutate({ enabled: next, own, operationId }, {
      onSuccess: () => {
        setOperationId(crypto.randomUUID())
        announce(next ? 'AI assistant turned on.' : 'AI assistant turned off.')
      },
    })
  }

  return (
    <section className="flex flex-col gap-4" aria-labelledby="assistant-settings-title">
      <header className="flex flex-col gap-1">
        <h2 id="assistant-settings-title" className="text-foreground text-base font-medium">AI assistant</h2>
        <p className="text-muted-foreground text-sm">
          Turn the assistant on or off for this entity. Subentities keep their own setting, and a
          platform-wide control can also turn it off.
        </p>
      </header>
      {definition.isError && (
        <RetryNotice message={assistantSettingErrorMessage(definition.error, 'load')} onRetry={() => void definition.refetch()} />
      )}
      {definition.isPending && <LoadingNotice message="Loading the assistant setting…" />}
      {definition.isSuccess && (
        <div className="flex flex-col gap-2">
          <label htmlFor={switchId} className="flex items-center gap-3 text-sm font-medium">
            <Switch id={switchId} checked={enabled} disabled={!canEdit} aria-describedby={hintId} onCheckedChange={change} />
            Use the AI assistant at this entity
          </label>
          <p id={hintId} className="text-muted-foreground text-xs">
            {own ? 'Set for this entity.' : 'On by default.'}
            {!canEdit && ' You do not have permission to change assistant settings.'}
          </p>
          <p role="status" className={applying ? 'text-muted-foreground text-xs' : 'sr-only'}>
            {applying ? 'Applying the setting. The assistant is unavailable here until it finishes.' : ''}
          </p>
          {result?.refreshStatus === 'Failed' && (
            <p role="alert" className="text-destructive text-xs">
              The setting couldn’t be applied. The assistant is unavailable here until it is.
            </p>
          )}
          {save.isError && <p role="alert" className="text-destructive text-sm">{assistantSettingErrorMessage(save.error)}</p>}
        </div>
      )}
    </section>
  )
}
